/**
 * Carousel
 * @module components/CarouselItem
 */

import { useState, useEffect, MouseEvent } from 'react';
import { CarouselItemModel, HeroBlockModel } from 'types/block-types';
import clsx from 'clsx';
import React from 'react';
import { getHeadingLevel } from 'common/helpers.js';
import { useSiteType } from 'context/siteType.context';
import Button from 'components/ui/Button';
import Icon from 'components/ui/Icon';
import HeroBlock from 'components/common/HeroBlock';

interface MediaProps {
	item: CarouselItemModel;
}

interface CarouselModel {
	item: CarouselItemModel | HeroBlockModel;
	headingLevel: number;
	headingStyleLevel: number;
	currentIndex: number;
	index: number;
	heightLevel: string;
	keyNav: boolean;
	setKeyNav: (value: boolean) => void;
}

const Media = (props: MediaProps) => {
	const { video, image, videoPosterImage } = props && props.item;
	const [canPlay, setCanPlay] = useState(false);

	const handleClick = (e: MouseEvent<HTMLVideoElement>) => {
		let videoElement = e.currentTarget;
		if (videoElement) {
			videoElement.paused ? videoElement.play() : videoElement.pause();
		}
	};

	if (video) {
		return (
			<div className="h-full w-full relative">
				{videoPosterImage && (
					<img
						className={clsx(
							'w-full max-h-300 object-cover object-center absolute h-auto',
							canPlay ? 'animate-fadeOut' : ''
						)}
						src={videoPosterImage}
						alt="placeholder loading video"
					/>
				)}
				<video
					playsInline
					className="w-full object-cover object-center md:max-h-full md:h-auto max-h-45"
					autoPlay
					preload="none"
					muted
					loop
					onClick={handleClick}
					onCanPlayThrough={(e) => {
						setCanPlay(true);
						if (e.currentTarget && e.currentTarget.play) {
							e.currentTarget.play();
						}
					}}
				>
					<source src={video} type="video/mp4" />
				</video>
			</div>
		);
	}
	if (image && image.src) {
		return (
			<picture>
				<source
					type="image/webp"
					srcSet={
						image.webpSizes &&
						`${image.webpSizes[0]} 1600w,${image.webpSizes[1]} 1200w, ${image.webpSizes[2]} 800w, ${image.webpSizes[3]} 400w`
					}
					sizes="(min-width: 768px) 60vw, (max-width: 767px) 70vw, 800px"
				/>
				<img
					className="h-full w-full object-cover sm:max-h-45 md:max-h-full"
					src={image.src}
					srcSet={
						image.sizes &&
						`${image.sizes[0]} 1600w,${image.sizes[1]} 1200w, ${image.sizes[2]} 800w, ${image.sizes[3]} 400w`
					}
					sizes="(min-width: 768px) 60vw, (max-width: 767px) 70vw, 800px"
					alt={image.alt}
				/>
			</picture>
		);
	}
	return null;
};

const CarouselItem = (props: CarouselModel) => {
	const {
		item,
		currentIndex,
		index,
		keyNav,
		setKeyNav,
		headingLevel,
		headingStyleLevel,
		heightLevel,
	} = props;

	const carouselItem = React.createRef() as React.RefObject<HTMLDivElement>;
	const carouselContentRef = React.createRef() as React.RefObject<HTMLDivElement>;
	const [focused, setFocused] = useState(currentIndex === index);
	const siteType = useSiteType();

	// This handles aria tags of the shadow copies the carousel creates to loop infinitely.
	useEffect(() => {
		let shouldFocus = currentIndex === index;
		if (
			carouselItem &&
			carouselItem.current &&
			carouselItem?.current?.parentElement?.parentElement?.classList.contains(
				'slick-cloned'
			)
		) {
			shouldFocus = false;
		}

		setFocused(shouldFocus);
		//eslint-disable-next-line
	}, [currentIndex, index, setFocused]);

	// If this is navigated to by keyboard focus the first paragraph in the slide.
	useEffect(() => {
		if (focused && keyNav) {
			carouselContentRef?.current?.focus();
			setKeyNav(false);
		}
		//eslint-disable-next-line
	}, [focused, keyNav, setKeyNav]);

	if (!item) return null;

	if (item.modelType === 'HeroBlock') {
		return <HeroBlock {...(item as HeroBlockModel)} isInsideCarousel />;
	}

	const carouselItemData = item as CarouselItemModel;
	const tabIndex = focused ? { tabIndex: 0 } : {};
	const Heading = getHeadingLevel(headingLevel);
	const headingCss =
		headingStyleLevel === 1
			? 'text-h2'
			: `text-${getHeadingLevel(headingStyleLevel)}`;
	return (
		<div
			className={clsx(
				'relative w-full h-auto',
				heightLevel === 'low' && 'md:h-45',
				heightLevel === 'medium' && 'md:h-65',
				heightLevel === 'normal' && 'md:h-120'
			)}
			style={{
				backgroundColor: carouselItemData.backgroundColor || 'transparent',
			}}
			ref={carouselItem}
		>
			{(heightLevel === 'normal' || heightLevel === 'medium') && (
				<Media item={carouselItemData} />
			)}
			{carouselItemData.heading && (
				<div
					className={clsx(
						'bg-transparent static max-w-297.5 my-auto mx-0 left-10 md:left-48 md:absolute',
						heightLevel === 'low' && 'md:py-5 md:px-0 p-4',
						heightLevel === 'medium' && 'top-1/10',
						heightLevel === 'normal' && 'top-1/5'
					)}
				>
					{heightLevel === 'normal' || heightLevel === 'medium' ? (
						<div
							className={clsx(
								'rounded-b-card md:rounded-card inline-block p-4 bg-white-whiteOpacity md:max-w-3xl relative max-w-full min-w-110',
								siteType === 'LindabGroup'
									? 'bg-lgDarkBlue text-lgLightGrayBlue md:py-4 md:px-6'
									: 'bg-blue md:p-10'
							)}
							{...tabIndex}
							ref={carouselContentRef}
						>
							<Heading
								className={clsx(
									'md:mb-2 mb-3 m-0',
									siteType === 'LindabGroup'
										? 'text-lgLightGrayBlue font-normal'
										: 'text-white font-bold',
									headingCss
								)}
							>
								{carouselItemData.heading}
							</Heading>
							{carouselItemData.description && (
								<p
									className={clsx(
										'md:mr-0 md:ml-0 md:mb-5 md:mt-2 font-standard font-normal md:text-p text-sm my-3 mx-0',
										siteType === 'LindabGroup'
											? 'text-lgLightGrayBlue'
											: 'text-white '
									)}
								>
									{carouselItemData.description}
								</p>
							)}
							{carouselItemData.button &&
								carouselItemData.button.text &&
								carouselItemData.button.link && (
									//@ts-ignore too much work for now
									<Button
										buttonColor={
											siteType === 'LindabGroup' ? 'lgYellow' : 'white'
										}
										url={carouselItemData.button.link}
										target={carouselItemData.button.target}
										tabIndex={focused ? 0 : -1}
										type="link"
										className={clsx(
											siteType === 'LindabGroup' && 'md:absolute m-0'
										)}
										noMargin
									>
										{siteType === 'LindabGroup' && (
											<Icon icon="chevrons" direction="left" size={1.25} />
										)}
										{carouselItemData.button.text}
										{siteType === 'Lindab' && <Icon icon="arrow" size={1.25} />}
									</Button>
								)}
						</div>
					) : (
						<div className="inline-block p-0 relative sm:max-w-full sm:py-1 sm:px-2 rounded-card">
							<Heading
								className={clsx(
									'font-normal mr-0 md:mb-3 ml-0 mb-6',
									headingCss,
									heightLevel === 'low' ? 'mb-3' : 'mt-6 mb-2',
									carouselItemData.backgroundColor === '#dadada'
										? 'text-black'
										: carouselItemData.backgroundColor === '#00426e'
										? 'text-lgLightGrayBlue'
										: 'text-white'
								)}
							>
								{carouselItemData.heading}
							</Heading>
							<p
								className={clsx(
									'md:mr-0 md:ml-0 font-standard font-normal text-black md:text-p text-sm my-3 mx-0',
									heightLevel === 'low' ? 'md:my-2' : 'my-4',
									carouselItemData.backgroundColor === '#dadada'
										? 'text-black'
										: carouselItemData.backgroundColor === '#00426e'
										? 'text-lgLightGray'
										: 'text-white'
								)}
							>
								{carouselItemData.description}
							</p>
							{carouselItemData.button && (
								<a
									className={clsx(
										'flex gap-1.5 font-standard font-normal text-black md:text-p hover:underline text-sm my-3 mx-0',
										carouselItemData.backgroundColor === '#dadada'
											? 'text-black'
											: carouselItemData.backgroundColor === '#00426e'
											? 'text-lgLightGray'
											: 'text-white'
									)}
									href={carouselItemData.button.link}
									target={carouselItemData.button.target}
									tabIndex={focused ? 0 : -1}
								>
									{siteType === 'LindabGroup' && (
										<Icon icon="chevrons" direction="left" size={1.25} />
									)}
									{carouselItemData.button.text}
									{siteType === 'Lindab' && <Icon icon="arrow" size={1.25} />}
								</a>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default CarouselItem;
